import React from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spinner,
  Switch,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { AddIcon, CheckIcon, CloseIcon, DeleteIcon, EditIcon, SearchIcon } from "@chakra-ui/icons";
import { FiFilter, FiMoreVertical } from "react-icons/fi";
import { MemoryFactProto } from "build-ts/memory";
import { epochConverterWithTimeZone } from "utils/dateUtils";

export type FilterMode = "all" | "active" | "inactive";

const filterLabel: Record<FilterMode, string> = {
  all: "All",
  active: "Active",
  inactive: "Inactive",
};

export interface AnalystMemoryFactsProps {
  // Data
  facts: MemoryFactProto[];
  loading: boolean;
  adding: boolean;
  savingId: string | null;
  deletingId: string | null;
  togglingId: string | null;
  defaultTimeZone: string;
  // Controlled UI state
  addText: string;
  editingId: string | null;
  editText: string;
  searchText: string;
  filterMode: FilterMode;
  // State callbacks
  onAddTextChange: (text: string) => void;
  onEditTextChange: (text: string) => void;
  onSearchTextChange: (text: string) => void;
  onFilterModeChange: (mode: FilterMode) => void;
  // Action callbacks
  onAdd: () => void;
  onToggleEnabled: (fact: MemoryFactProto) => void;
  onStartEdit: (fact: MemoryFactProto) => void;
  onCancelEdit: () => void;
  onSaveEdit: (fact: MemoryFactProto) => void;
  onDelete: (factId: string) => void;
}

const AnalystMemoryFacts: React.FC<AnalystMemoryFactsProps> = ({
  facts,
  loading,
  adding,
  savingId,
  deletingId,
  togglingId,
  defaultTimeZone,
  addText,
  editingId,
  editText,
  searchText,
  filterMode,
  onAddTextChange,
  onEditTextChange,
  onSearchTextChange,
  onFilterModeChange,
  onAdd,
  onToggleEnabled,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}) => {
  const enabledCount = facts.filter((f) => f.enabled).length;

  const visibleFacts = facts.filter((f) => {
    const matchesFilter =
      filterMode === "all" ||
      (filterMode === "active" && f.enabled) ||
      (filterMode === "inactive" && !f.enabled);
    const matchesSearch =
      !searchText.trim() ||
      (f.text ?? "").toLowerCase().includes(searchText.trim().toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <Box height="100%" bg="white" p={4} display="flex" flexDirection="column">
      {/* Header */}
      <Box maxW="900px" w="100%" mx="auto" mb={3}>
        <HStack spacing={2} align="center" justify="space-between">
          {/* Search bar */}
          <InputGroup size="md" maxW="50%">
            <InputLeftElement pointerEvents="none" height="full">
              <SearchIcon color="gray.400" boxSize={4} />
            </InputLeftElement>
            <Input
              placeholder="Search memory..."
              value={searchText}
              onChange={(e) => onSearchTextChange(e.target.value)}
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              fontSize="sm"
              _hover={{ borderColor: "gray.300", bg: "white" }}
              _focus={{
                borderColor: "purple.400",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                bg: "white",
              }}
              _placeholder={{ color: "gray.400" }}
            />
          </InputGroup>

          {/* Filter menu */}
          <Menu closeOnSelect>
            <MenuButton
              as={Button}
              size="sm"
              variant={filterMode !== "all" ? "solid" : "outline"}
              colorScheme={filterMode !== "all" ? "purple" : "gray"}
              bg={filterMode !== "all" ? "purple.700" : "gray.100"}
              leftIcon={<Icon as={FiFilter} boxSize={4} />}
              fontWeight="600"
              flexShrink={0}
              px={2}
              py={1}
            >
              {filterLabel[filterMode]}
            </MenuButton>
            <MenuList minW="160px" fontSize="sm">
              <MenuItem
                onClick={() => onFilterModeChange("all")}
                fontWeight={filterMode === "all" ? "600" : "normal"}
              >
                All ({facts.length})
              </MenuItem>
              <MenuDivider />
              <MenuItem
                onClick={() => onFilterModeChange("active")}
                fontWeight={filterMode === "active" ? "600" : "normal"}
              >
                Active ({enabledCount})
              </MenuItem>
              <MenuItem
                onClick={() => onFilterModeChange("inactive")}
                fontWeight={filterMode === "inactive" ? "600" : "normal"}
              >
                Inactive ({facts.length - enabledCount})
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>

        {/* Add fact row */}
        <HStack spacing={2} mt={4} mb={4} align="center">
          <Textarea
            size="md"
            value={addText}
            onChange={(e) => onAddTextChange(e.target.value)}
            placeholder="Add to memory..."
            rows={2}
            resize="none"
            borderColor="gray.300"
            _focus={{
              borderColor: "purple.700",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)",
            }}
            _hover={{ bg: "white" }}
            _placeholder={{ color: "gray.600", fontWeight: "normal" }}
            fontWeight="600"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onAdd();
              }
            }}
          />
          {addText.trim() && (
            <Tooltip label="Add to memory">
              <IconButton
                aria-label="Add memory fact"
                icon={adding ? <Spinner size="xs" /> : <AddIcon boxSize={3} />}
                size="sm"
                colorScheme="purple"
                bg="purple.600"
                variant="solid"
                isDisabled={!addText.trim() || adding}
                onClick={onAdd}
              />
            </Tooltip>
          )}
        </HStack>

        <Divider />
      </Box>

      {/* List */}
      <Box maxW="900px" w="100%" mx="auto" flex="1" minH={0} display="flex" flexDirection="column">
        <Box flex="1" minH={0} overflowY="auto">
          {loading ? (
            <Flex justify="center" py={4}>
              <Spinner size="sm" color="purple.500" />
            </Flex>
          ) : facts.length === 0 ? (
            <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>
              No memory facts yet. Add one below to give this analyst persistent guidance.
            </Text>
          ) : visibleFacts.length === 0 ? (
            <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>
              No facts match your search or filter.
            </Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {visibleFacts.map((fact) => {
                const isEditing = editingId === fact.id;
                const isSaving = savingId === fact.id;
                const isDeleting = deletingId === fact.id;
                const isToggling = togglingId === fact.id;
                const dateAdded = fact.created_at
                  ? epochConverterWithTimeZone(fact.created_at, "MMM d, yyyy", defaultTimeZone)
                  : null;

                return (
                  <Box
                    key={fact.id}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={fact.enabled ? "gray.400" : "gray.200"}
                    bg={fact.enabled ? "white" : "gray.50"}
                    px={3}
                    py={2}
                    transition="all 0.15s"
                  >
                    {isEditing ? (
                      <VStack align="stretch" spacing={2}>
                        <Textarea
                          size="sm"
                          value={editText}
                          onChange={(e) => onEditTextChange(e.target.value)}
                          rows={3}
                          resize="vertical"
                          autoFocus
                          _focus={{
                            borderColor: "purple.500",
                            boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                          }}
                        />
                        <HStack justify="flex-end" spacing={2}>
                          <Button
                            size="xs"
                            variant="ghost"
                            leftIcon={<CloseIcon boxSize={2} />}
                            onClick={onCancelEdit}
                            isDisabled={isSaving}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="purple"
                            leftIcon={
                              isSaving ? <Spinner size="xs" /> : <CheckIcon boxSize={2} />
                            }
                            onClick={() => onSaveEdit(fact)}
                            isDisabled={!editText.trim() || isSaving}
                          >
                            Save
                          </Button>
                        </HStack>
                      </VStack>
                    ) : (
                      <HStack justify="space-between" align="flex-start" spacing={4}>
                        {/* Toggle */}
                        <Box pt="3px" flexShrink={0}>
                          <Tooltip
                            label={fact.enabled ? "Toggle to disable" : "Toggle to enable"}
                            placement="top"
                            shouldWrapChildren
                          >
                            <Switch
                              size="sm"
                              colorScheme="purple"
                              isChecked={fact.enabled ?? false}
                              isDisabled={isToggling}
                              onChange={() => onToggleEnabled(fact)}
                            />
                          </Tooltip>
                        </Box>

                        {/* Text + date */}
                        <Box flex={1}>
                          <Text
                            fontSize="sm"
                            color={fact.enabled ? "gray.800" : "gray.500"}
                            mb={0}
                            fontWeight={fact.enabled ? "600" : "normal"}
                            textAlign="left"
                            wordBreak="break-word"
                          >
                            {fact.text}
                          </Text>
                          <HStack spacing={2} align="center" justify="space-between">
                            {dateAdded && (
                              <Text
                                fontSize="xs"
                                color="gray.400"
                                mt={0.5}
                                mb={0}
                                textAlign="left"
                              >
                                {dateAdded}
                              </Text>
                            )}
                            // To-Do : Need User List API.
                            {/* {
                              fact.added_by && (
                                <Text fontSize="xs" color="gray.400" mt={0.5} mb={0} textAlign="left">
                                  By {fact.added_by}
                                </Text>
                              )
                            } */}
                          </HStack>
                        </Box>

                        {/* Three-dot menu */}
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            aria-label="More options"
                            icon={<Icon as={FiMoreVertical} boxSize={4} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="gray"
                            flexShrink={0}
                          />
                          <MenuList minW="120px" fontSize="sm">
                            <MenuItem
                              icon={<EditIcon />}
                              onClick={() => onStartEdit(fact)}
                            >
                              Edit
                            </MenuItem>
                            <MenuDivider my={0} />
                            <MenuItem
                              icon={isDeleting ? <Spinner size="xs" /> : <DeleteIcon />}
                              onClick={() => fact.id && onDelete(fact.id)}
                              color="red.500"
                              isDisabled={isDeleting}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    )}
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AnalystMemoryFacts;
